import type { EntryKind } from '@taler/api-client'
import {
  absMoney,
  addMoney,
  type CurrencyCode,
  equalsLocalDate,
  type LocalDate,
  type Money,
} from '@taler/domain'
import type { TransactionRow } from './transaction-row.ts'

/** One kind's total for one currency. Kinds never mix currencies - that would be the
 * client-side conversion the project forbids - so a day with EUR and HUF expenses gets
 * two entries here, not one. */
export interface KindSubtotal {
  readonly kind: Exclude<EntryKind, undefined>
  readonly currency: CurrencyCode
  readonly total: Money
}

export interface DayGroup {
  readonly date: LocalDate
  readonly rows: readonly TransactionRow[]
  /** True only for the last group when another page may still extend this same day -
   * a subtotal computed now would be a partial figure presented as a complete one. */
  readonly isPartial: boolean
  /** Empty while `isPartial` - an incomplete day's subtotal cannot be told apart from a
   * complete one once rendered, so it is not rendered at all. */
  readonly subtotals: readonly KindSubtotal[]
}

function subtotalsFor(rows: readonly TransactionRow[]): KindSubtotal[] {
  const byKey = new Map<string, KindSubtotal>()
  for (const row of rows) {
    // A valuation moved no money, so it never adds to a day's kind totals.
    if (row.kind === undefined || row.total === undefined || row.isRevaluation) continue
    // A transfer's total is a magnitude, never a signed net - the direction lives in
    // the row's own account column, not in this figure.
    const amount = row.kind === 'TRANSFER' ? absMoney(row.total) : row.total
    const key = `${row.kind}:${amount.currency}`
    const existing = byKey.get(key)
    byKey.set(key, {
      kind: row.kind,
      currency: amount.currency,
      total: existing === undefined ? amount : addMoney(existing.total, amount),
    })
  }
  return [...byKey.values()]
}

/**
 * Rows arrive newest-first with the id as a total-order tiebreak (contract §4), so once
 * the stream has moved past a date it can never recur - only the very last loaded group
 * can still be missing rows a further page holds.
 */
export function groupByDay(rows: readonly TransactionRow[], hasNextPage: boolean): DayGroup[] {
  const groups: { date: LocalDate; rows: TransactionRow[] }[] = []
  for (const row of rows) {
    const current = groups[groups.length - 1]
    if (current !== undefined && equalsLocalDate(current.date, row.date)) {
      current.rows.push(row)
    } else {
      groups.push({ date: row.date, rows: [row] })
    }
  }
  return groups.map((group, index) => {
    const isPartial = hasNextPage && index === groups.length - 1
    return {
      date: group.date,
      rows: group.rows,
      isPartial,
      subtotals: isPartial ? [] : subtotalsFor(group.rows),
    }
  })
}
